import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";
import { api } from "@lr-notitia/notitia-operator/client";
import type { InstanceWorkspace, Graph, ChildProcessStats } from "@lr-notitia/notitia-operator/schemas";
import { handleGraphModification } from "./graph/utils";
import { useGraphStore } from "./graph";
import { useDomainStore } from "./domain";
import { useGptQuestionsSchemaSpecializationStore } from "./gpt/questionsSchemaSpecialization";
import { UserRole, Positions } from "@lr-notitia/notitia-operator/schemas";
import { graph } from "../modules/workspaces/utils/graph";
import router from "../router";

let graphStore: ReturnType<typeof useGraphStore>;
let domainStore: ReturnType<typeof useDomainStore>;
let gptQuestionsSchemaSpecializationStore: ReturnType<typeof useGptQuestionsSchemaSpecializationStore>;
function init() {
	graphStore = useGraphStore();
	domainStore = useDomainStore();
	gptQuestionsSchemaSpecializationStore = useGptQuestionsSchemaSpecializationStore();
}

export const useInstanceWorkspacesStore = defineStore("InstanceWorkspaces", () => {
	const workspaces = ref<InstanceWorkspace[]>([]);
	const workspaceDescriptors = ref<{
		[dataspaceId: string]: { [workspaceId: string]: { label: string; description: string } };
	}>({});

	function loadWorkspace(domainId: string, workspaceId: string): Promise<void> {
		return new Promise(async (resolve) => {
			let workspace = await api().domain(domainId).workspace(workspaceId).get();
			if (!workspace) return console.warn("Failed to load workspace");
			if (!workspaces.value.find((d) => d.id === workspace.id)) workspaces.value.push(workspace);
			resolve();
		});
	}
	function clearWorkspaces() {
		if (!domainStore) init();
		api().socket().domain(domainStore.domain.id).instance().workspaces().off();
		workspaces.value = [];
	}
	function getById(id: string): InstanceWorkspace | undefined {
		return workspaces.value.find((workspace) => workspace.id === id);
	}
	async function addWorkspace(
		dataspaceId: string,
		templateId: string,
		name: string,
		description: string = "",
		newId: string | undefined = undefined
	): Promise<{ id: string; graph: Graph }> {
		if (!domainStore) init();
		return new Promise((resolve, reject) => {
			if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
			api()
				.domain(domainStore.domain.id)
				.workspaces()
				.post(dataspaceId, templateId, name, description, newId)
				.then((data) => {
					workspaces.value.push(data.graph.nodes[data.id] as unknown as InstanceWorkspace);
					resolve(data);
				})
				.catch(() => reject());
		});
	}

	function updateWorkspace(workspace: InstanceWorkspace): Promise<InstanceWorkspace> {
		if (!graphStore || domainStore) init();
		return new Promise((resolve, reject) => {
			if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
			api()
				.domain(domainStore.domain.id)
				.workspace(workspace.id)
				.put(workspace)
				.then((data) => {
					handleGraphModification(data.modification, data.metadata);
					resolve(data.modification[workspace.id] as InstanceWorkspace);
				})
				.catch(() => reject());
		});
	}
	function updateNodePositions(positions: Positions): Promise<Positions> {
		if (!domainStore) init();
		return new Promise((resolve, reject) => {
			if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
			if (!openedWorkspaceId.value) return reject();
			api()
				.domain(domainStore.domain.id)
				.workspace(openedWorkspaceId.value)
				.positions()
				.put(positions)
				.then((data) => {
					nodePositions.value[openedWorkspaceId.value!] = {
						...nodePositions.value[openedWorkspaceId.value!],
						...data,
					};
					resolve(data);
				})
				.catch(() => reject());
		});
	}

	function removeWorkspace(id: string): Promise<void> {
		if (!domainStore) init();
		return new Promise((resolve, reject) => {
			if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
			api()
				.domain(domainStore.domain.id)
				.workspace(id)
				.del()
				.then(() => {
					workspaces.value = workspaces.value.filter((workspace) => workspace.id !== id);
					resolve();
				})
				.catch(() => reject());
		});
	}

	function loadWorkspaceDescriptors(domainId: string, dataspaceId: string): Promise<void> {
		return new Promise((resolve, reject) => {
			api()
				.domain(domainId)
				.dataspace(dataspaceId)
				.workspaceDescriptors()
				.get()
				.then((data) => {
					workspaceDescriptors.value[dataspaceId] = data;
					resolve();
				})
				.catch(() => console.warn("Failed to load workspace descriptors"));
		});
	}
	function setWorkspaceDescriptor(
		domainId: string,
		dataspaceId: string,
		workspaceId: string,
		label: string,
		description: string
	): Promise<void> {
		return new Promise((resolve, reject) => {
			if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
			api()
				.domain(domainId)
				.dataspace(dataspaceId)
				.workspaceDescriptors()
				.post(workspaceId, label, description)
				.then(() => {
					if (!workspaceDescriptors.value[dataspaceId]) workspaceDescriptors.value[dataspaceId] = {};
					workspaceDescriptors.value[dataspaceId][workspaceId] = { label, description };
					resolve();
				})
				.catch(() => reject());
		});
	}
	function deleteWorkspaceDescriptor(domainId: string, dataspaceId: string, workspaceId: string): Promise<void> {
		return new Promise((resolve, reject) => {
			if (!domainStore.hasAccessRights(UserRole.Editor)) return reject();
			api()
				.domain(domainId)
				.dataspace(dataspaceId)
				.workspaceDescriptors()
				.del(workspaceId)
				.then(() => {
					if (!workspaceDescriptors.value[dataspaceId]) workspaceDescriptors.value[dataspaceId] = {};
					delete workspaceDescriptors.value[dataspaceId][workspaceId];
					resolve();
				})
				.catch(() => reject());
		});
	}

	const openedWorkspaceId = ref<string | undefined>(undefined);
	const openedWorkspace = computed(() => {
		if (!openedWorkspaceId.value) return undefined;
		return getById(openedWorkspaceId.value);
	});
	const selectedNodeId = ref<string>("");

	const workspaceGraph = computed(() => {
		if (!openedWorkspaceId.value) return { nodes: [], links: [] };
		return graph(openedWorkspaceId.value || "", selectedNodeId.value);
	});
	const nodePositions = ref<{ [workspaceId: string]: Positions }>({});
	const progressActive = ref(false);
	const expandedNotifications = ref<string[]>([]);

	function getReferencePosition(nodeId: string) {
		// always 0,0 for now, can be changed in the future to use
		// - 0,0 for root nodes
		// - the position of the parent node (considering where the new node whill be connected to with hasA)
		return { x: 0, y: 0 };
	}
	function checkOverlap(x: number, y: number, minSeparation: number) {
		for (const pos of Object.values(nodePositions.value[openedWorkspaceId.value!])) {
			const distance = Math.sqrt(Math.pow(x - (pos.x || 0), 2) + Math.pow(y - pos.y, 2));
			if (distance < minSeparation) return true;
		}
		return false;
	}
	function newNodePos(nodeId: string) {
		if (!openedWorkspace.value) return { x: 0, y: 0 };
		if (!graphStore) init();
		let referencePos = getReferencePosition(nodeId);

		let instanceDataobject = graphStore.instance.dataobject.byId(nodeId);
		if (!instanceDataobject) return { x: 0, y: 0 };
		let templateDataobject = graphStore.template.dataobject.byId(instanceDataobject.template);
		if (!templateDataobject) return { x: 0, y: 0 };
		let templateWorkspaceId = openedWorkspace.value.template;
		let templateDataobjectPos = templateDataobject.context?.["nta-pos"]?.[templateWorkspaceId] || { x: 0, y: 0 };

		let baseX = templateDataobjectPos.x - referencePos.x;
		let baseY = templateDataobjectPos.y - referencePos.y;

		const NODE_SEPARATION = 300;

		// First try the template position
		if (!checkOverlap(baseX, baseY, NODE_SEPARATION)) {
			return { x: baseX, y: baseY };
		}

		// Otherwise, create a grid around the base position
		const NODES_PER_ROW = 3;
		const NODE_SPACING = 400;
		let nodeCount = 0;
		let maxTries = 50;

		while (maxTries > 0) {
			nodeCount++;
			const row = Math.floor((nodeCount - 1) / NODES_PER_ROW);
			const col = (nodeCount - 1) % NODES_PER_ROW;

			// Position relative to the base position
			let deltaX = baseX + col * NODE_SPACING;
			let deltaY = baseY + row * NODE_SPACING;

			if (!checkOverlap(deltaX, deltaY, NODE_SEPARATION)) {
				return { x: deltaX, y: deltaY };
			}

			maxTries--;
		}

		// If we can't find a spot, just use the base position
		return { x: baseX, y: baseY };
	}

	async function getStats(): Promise<Record<string, ChildProcessStats>> {
		if (!domainStore) init();
		const stats = (await api().domain(domainStore.domain.id).workspaces().stats().get()) || {};
		return stats;
	}

	watch(
		() => workspaces.value,
		() => {
			if (!graphStore) init();
			workspaces.value.sort((a, b) => {
				if (a.label < b.label) return -1;
				if (a.label > b.label) return 1;
				return 0;
			});
		}
	);
	watch(
		() => openedWorkspaceId.value,
		(newId, oldId) => {
			if (!gptQuestionsSchemaSpecializationStore) init();
			gptQuestionsSchemaSpecializationStore.showHardcodedNotification = false;
			let domainId = router.currentRoute.value.params.domainId as string;
			if (!domainStore) init();
			if (oldId) {
				api().socket().domain(domainId).instance().workspace(oldId).off();
			}
			if (newId) {
				api()
					.socket()
					.domain(domainId)
					.instance()
					.workspace(newId)
					.on((message) => {
						const d = workspaces.value?.find((workspace) => workspace.id === message.id);
						if (!d) return;
						d.label = message.label;
						d.dataobjects = message.dataobjects;
					});

				api()
					.domain(domainId)
					.workspace(newId)
					.positions()
					.get()
					.then((data) => (nodePositions.value[openedWorkspaceId.value!] = data))
					.catch();
			}
		}
	);

	return {
		workspaces,
		loadWorkspace,
		clearWorkspaces,
		getById,
		nodePositions,
		addWorkspace,
		updateWorkspace,
		updateNodePositions,
		removeWorkspace,
		openedWorkspaceId,
		openedWorkspace,
		workspaceGraph,
		selectedNodeId,
		progressActive,
		expandedNotifications,
		workspaceDescriptors,
		loadWorkspaceDescriptors,
		setWorkspaceDescriptor,
		deleteWorkspaceDescriptor,
		newNodePos,
		getStats,
	};
});
